import React, {
  useState, useEffect, useContext, useRef,
} from 'react';
import {
  Autocomplete,
  Box,
  Button, Container, FormControlLabel, FormHelperText, Switch, TextField,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import PhoneInput from 'react-phone-input-2';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import 'react-phone-input-2/lib/style.css';
import { useTranslation } from 'react-i18next';
import Elements from '../../../Elements';
import { insertAddress, updateAddress } from '../../../../Redux/Models/address';
import { getUserData } from '../../../../Utils/storages';
import AlertContext from '../../../../Context/AlertContext';

const DisplayingErrorMessagesSchema = Yup.object().shape({
  name: Yup.string().required('Name field is required'),
  label: Yup.string().required('Label field is required'),
  phone_number: Yup.string().required('Phone number field is required'),
  country: Yup.string().required('Country or region is required'),
  province: Yup.string().required('State or province is required'),
  address: Yup.string().required('Address field is required'),
  city: Yup.string().required('City field is required'),
  postal_code: Yup.string().required('Zip code field is required'),
});

export default function UpdateAddress({
  classes, isLoading, message, error, newAddress, data,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userData = getUserData();
  const { openAlert } = useContext(AlertContext);
  const { addressId } = useParams();
  const { t } = useTranslation();

  const fieldRefs = {
    name: useRef(),
    label: useRef(),
    phone_number: useRef(),
    country: useRef(),
    province: useRef(),
    address: useRef(),
    city: useRef(),
    postal_code: useRef(),
  };

  const [openConfirm, setOpenConfirm] = useState(false);
  const [checked, setChecked] = useState(false);

  const handleCheck = (event) => {
    setChecked(event.target.checked);
  };

  const toggleConfirm = (e) => {
    e.preventDefault();
    setOpenConfirm(!openConfirm);
  };

  const [submitted, setSubmitted] = useState(false);

  const formik = useFormik({
    initialValues: {
      address: '',
      country: '',
      city: '',
      postal_code: '',
      province: '',
      name: '',
      label: '',
      address_2: '',
      phone_number: '',
    },
    validationSchema: DisplayingErrorMessagesSchema,
    onSubmit: () => {
      setOpenConfirm(true);
    },
  });

  const handleSubmit = () => {
    setSubmitted(true);
    if (addressId) {
      // If this is the only address it will be mainAddress
      const mainAddress = checked || data.length === 1;
      dispatch(updateAddress(userData.id, addressId, formik.values, mainAddress));
    } else {
      // If this is new or 1st address it will be mainAddress
      const mainAddress = checked || !data.length;
      dispatch(insertAddress(userData.id, formik.values, mainAddress));
    }
  };

  const [selectedCountry, setSelectedCountry] = useState();
  const [selectedState, setSelectedState] = useState();
  const [selectedCity, setSelectedCity] = useState('');
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const [loadCountries, setLoadCountries] = useState(false);
  const [loadCities, setLoadCities] = useState(false);

  const changePhoneNumber = (phone) => {
    formik.handleChange({ target: { name: 'phone_number', value: phone } });
  };

  useEffect(() => {
    if (selectedCountry?.name && selectedState?.name) {
      setLoadCities(true);
      const body = {
        country: selectedCountry.name,
        state: selectedState.name,
      };
      axios.post('https://countriesnow.space/api/v0.1/countries/state/cities', body)
        .then((res) => {
          if (!res.data.error) {
            setCities(res.data.data);
          }
          setLoadCities(false);
        });
    }
  }, [selectedCountry, selectedState]);

  useEffect(() => {
    const firstError = Object.keys(formik.errors)?.[0];
    if (firstError) {
      fieldRefs[firstError]?.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }, [formik.errors, formik.touched]);

  useEffect(() => {
    if (error && submitted) {
      openAlert({
        open: true,
        severity: 'error',
        message: `Something went wrong, ${message}`,
      });
    }
  }, [error, submitted]);

  useEffect(() => {
    if (submitted && newAddress) {
      navigate(-1, 'replace');
    }
  }, [submitted, newAddress]);

  const getCountries = () => {
    setLoadCountries(true);
    axios.get('https://countriesnow.space/api/v0.1/countries/states')
      .then((res) => {
        if (!res.data.error) {
          setCountries(res.data.data);
        }
        setLoadCountries(false);
      });
  };

  useEffect(() => {
    if (addressId) {
      const filteredAdress = data
        .filter((address) => address.id === addressId);
      if (filteredAdress.length) {
        formik.setValues({ ...formik.values, ...filteredAdress[0] });
        setSelectedCity(filteredAdress[0]?.city);
        setChecked(filteredAdress[0].is_default);

        if (countries.length) {
          const currentCountry = countries.filter((c) => c.name === filteredAdress[0].country);
          if (currentCountry.length) {
            setSelectedCountry(currentCountry[0]);
            setStates(currentCountry[0]?.states);

            const currentState = currentCountry[0]?.states
              ?.filter((s) => s.name === filteredAdress[0].province);
            if (currentState.length) {
              setSelectedState(currentState[0]);
            }
          }
        }
      }
    }
  }, [addressId, countries]);

  useEffect(() => {
    getCountries();
  }, []);

  return (
    <Elements.PageBase navigate={navigate} noNav>
      <div className={classes.root}>
        <Elements.Navigator title={addressId ? t('address.update') : t('address.create')} />

        <div className="mt-2">
          <form onSubmit={formik.handleSubmit} autoComplete="off">
            <Container>
              <div className="mt-4 weight-700 s-14" ref={fieldRefs.name}>
                {t('address.name')}
                <span className="primary-green">*</span>
              </div>
              <TextField
                size="small"
                sx={{ mt: 1 }}
                value={formik.values.name}
                name="name"
                onChange={formik.handleChange}
                fullWidth
                multiline
                placeholder="Your full name"
                error={formik.touched.name && formik.errors.name}
              />
              {(formik.touched.name && formik.errors.name)
                && (
                <FormHelperText error sx={{ fontSize: 14 }}>
                  {formik.errors.name}
                </FormHelperText>
                )}

              <div className="mt-3 weight-700 s-14" ref={fieldRefs.label}>
                {t('address.label')}
                <span className="primary-green">*</span>
              </div>
              <TextField
                size="small"
                sx={{ mt: 1 }}
                value={formik.values.label}
                name="label"
                onChange={formik.handleChange}
                fullWidth
                multiline
                placeholder="Home/Apartment"
                error={formik.touched.label && formik.errors.label}
              />
              {(formik.touched.label && formik.errors.label)
                && (
                <FormHelperText error sx={{ fontSize: 14 }}>
                  {formik.errors.label}
                </FormHelperText>
                )}

              <div className="mt-3 weight-700 s-14" ref={fieldRefs.phone_number}>
                {t('address.phone')}
                <span className="primary-green">*</span>
              </div>
              <PhoneInput
                placeholder="Your phone number"
                value={formik.values.phone_number}
                onChange={(phone, e) => changePhoneNumber(phone, e)}
                // autoFormat={false}
                // enableSearch
                country="fr"
                containerStyle={{
                  width: '100%',
                  marginTop: 8,
                }}
                inputStyle={{
                  width: '100%',
                  borderRadius: 8,
                  height: 40,
                }}
                buttonStyle={{
                  borderTopLeftRadius: 8,
                  borderBottomLeftRadius: 8,
                  background: 'transparent',
                }}
              />
              {(formik.touched.phone_number && formik.errors.phone_number)
                && (
                <FormHelperText error sx={{ fontSize: 14 }}>
                  {formik.errors.phone_number}
                </FormHelperText>
                )}

              <div className="mt-3 weight-700 s-14" ref={fieldRefs.country}>
                {t('address.country')}
                <span className="primary-green">*</span>
              </div>
              <div className="mt-2">
                <Autocomplete
                  options={countries}
                  value={selectedCountry}
                  loading={loadCountries}
                  onChange={(e, newVal) => {
                    setSelectedCountry(newVal);
                    setStates(newVal.states);
                    formik.handleChange({ target: { name: 'country', value: newVal.name } });
                  }}
                  inputValue={formik.values.country}
                  onInputChange={(e) => {
                    if (e) {
                      formik.handleChange(e);
                    }
                  }}
                  getOptionLabel={(selected) => selected.name}
                  getOptionSelected={(option, value) => option.iso3 === value.iso3}
                  renderOption={(optProp, opt) => (
                    <Box key={opt.iso3} component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...optProp}>
                      <img
                        loading="lazy"
                        width="20"
                        src={`https://flagcdn.com/w20/${opt?.iso2?.toLowerCase()}.png`}
                        srcSet={`https://flagcdn.com/w40/${opt?.iso2?.toLowerCase()}.png 2x`}
                        alt=""
                      />
                      {opt.name}
                      {' '}
                      (
                      {opt.iso3}
                      )
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Choose country"
                      size="small"
                      name="country"
                      error={formik.touched.country && formik.errors.country}
                      InputProps={{
                        ...params.InputProps,
                      }}
                    />
                  )}
                />
                {(formik.touched.country && formik.errors.country)
                && (
                <FormHelperText error sx={{ fontSize: 14 }}>
                  {formik.errors.country}
                </FormHelperText>
                )}
              </div>

              <Box mt={2} display="flex" justifyContent="space-between" ref={fieldRefs.address}>
                <div className="weight-700 s-14">
                  {t('address.street1')}
                  <span className="primary-green">*</span>
                </div>
                <div className="mute s-12"><i>e.g. Jhonberg street No. 12A</i></div>
              </Box>
              <TextField
                size="small"
                sx={{ mt: 1 }}
                value={formik.values.address}
                name="address"
                onChange={formik.handleChange}
                fullWidth
                multiline
                placeholder="Your address"
                inputProps={{
                  autocomplete: 'new-password',
                }}
              />
              {(formik.touched.address && formik.errors.address)
                && (
                <FormHelperText error sx={{ fontSize: 14 }}>
                  {formik.errors.address}
                </FormHelperText>
                )}

              <Box mt={2} display="flex" justifyContent="space-between">
                <div className="weight-700 s-14">
                  {t('address.street2')}
                </div>
                <div className="mute s-12"><i>e.g. 4th floor</i></div>
              </Box>
              <TextField
                size="small"
                sx={{ mt: 1 }}
                value={formik.values.address_2}
                name="address_2"
                onChange={formik.handleChange}
                fullWidth
                multiline
                placeholder="Street address"
                inputProps={{
                  autocomplete: 'new-password',
                }}
              />

              <div className="mt-3 weight-700 s-14" ref={fieldRefs.province}>
                {t('address.state')}
                <span className="primary-green">*</span>
              </div>
              <div className="mt-2">
                <Autocomplete
                  options={states}
                  value={selectedState}
                  loading={loadCountries}
                  onChange={(e, newVal) => {
                    setSelectedState(newVal);
                    formik.handleChange({ target: { name: 'province', value: newVal.name } });
                  }}
                  inputValue={formik.values.province}
                  onInputChange={(e) => {
                    if (e) {
                      formik.handleChange(e);
                    }
                  }}
                  getOptionLabel={(selected) => selected.name}
                  getOptionSelected={(option, value) => option.state_code === value.state_code}
                  renderOption={(optProp, opt) => (
                    <Box key={opt.state_code} {...optProp}>
                      {opt.name}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      name="province"
                      placeholder="Choose state/province"
                      error={formik.touched.province && formik.errors.province}
                      InputProps={{
                        ...params.InputProps,
                      }}
                    />
                  )}
                />
                {(formik.touched.province && formik.errors.province)
                && (
                <FormHelperText error sx={{ fontSize: 14 }}>
                  {formik.errors.province}
                </FormHelperText>
                )}
              </div>

              <div className="mt-3 weight-700 s-14" ref={fieldRefs.city}>
                {t('address.city')}
                <span className="primary-green">*</span>
              </div>
              <div className="mt-2">
                <Autocomplete
                  options={cities}
                  value={selectedCity || ''}
                  loading={loadCities}
                  onChange={(e, newVal) => {
                    setSelectedCity(newVal);
                    formik.handleChange({ target: { name: 'city', value: newVal } });
                  }}
                  inputValue={formik.values.city || ''}
                  onInputChange={(e) => {
                    if (e) {
                      formik.handleChange(e);
                    }
                  }}
                  renderOption={(optProp, opt) => (
                    <Box key={opt} {...optProp}>
                      {opt}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Choose city"
                      name="city"
                      error={formik.touched.city && formik.errors.city}
                      InputProps={{
                        ...params.InputProps,
                      }}
                    />
                  )}
                />
                {(formik.touched.city && formik.errors.city)
                && (
                <FormHelperText error sx={{ fontSize: 14 }}>
                  {formik.errors.city}
                </FormHelperText>
                )}
              </div>

              <div className="mt-3 weight-700 s-14" ref={fieldRefs.postal_code}>
                {t('address.zip')}
                <span className="primary-green">*</span>
              </div>
              <div className="mt-2">
                <TextField
                  size="small"
                  value={formik.values.postal_code}
                  name="postal_code"
                  onChange={formik.handleChange}
                  fullWidth
                  placeholder="Your postal code"
                  error={formik.touched.postal_code && formik.errors.postal_code}
                  inputProps={{
                    autocomplete: 'new-password',
                  }}
                />
                {(formik.touched.postal_code && formik.errors.postal_code)
                  && (
                  <FormHelperText error sx={{ fontSize: 14 }}>
                    {formik.errors.postal_code}
                  </FormHelperText>
                  )}
              </div>

              <div className="mt-2" hidden={formik.values?.is_default}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={checked}
                      onChange={handleCheck}
                      inputProps={{ 'aria-label': 'controlled' }}
                    />
                  )}
                  label="Main address"
                />
              </div>

              <Box mt={2}>
                <Elements.Info message={(
                  <span>
                    {t('address.agree')}
                  </span>
              )}
                />
              </Box>
            </Container>

            <div className="floating-action">
              <Container>
                <Button fullWidth type="submit" size="large" variant="contained" className="btn-contained">
                  {t('common.confirm')}
                </Button>
              </Container>
            </div>
          </form>
        </div>
      </div>

      <Elements.ConfirmationModal
        open={openConfirm}
        desc={(
          <div>
            Make sure your address
            information is correct
          </div>
        )}
        title="Save address"
        handleAgree={handleSubmit}
        handleClose={toggleConfirm}
        isLoading={isLoading}
      />
    </Elements.PageBase>
  );
}
